import { Button, TextButtonLink } from '@creditornot/cb-button';
import { elevationCss } from '@creditornot/cb-ingredients';
import React, { MouseEventHandler } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { typographyCss } from 'common/typography';

import ResponsiveContainer from './ResponsiveContainer';
import { MOBILE_BREAKPOINT } from '../../../common/hooks/useIsMobile';

export type CookieConsentsModalProps = {
  className?: string;
  onAcceptDefaultConsentsClick: MouseEventHandler<HTMLButtonElement>;
  onManageConsentsClick: MouseEventHandler<HTMLButtonElement>;
  show: boolean;
  privacyPolicyUrl: string | undefined;
};

type CookieConsentsBannerProps = {
  className?: string;
  onAcceptDefaultConsentsClick: MouseEventHandler<HTMLButtonElement>;
  onManageConsentsDialogClick: MouseEventHandler<HTMLButtonElement>;
  privacyPolicyUrl: string | undefined;
};

const Root = styled.div`
  z-index: 100001;
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;

const BannerContainer = styled(ResponsiveContainer)`
  position: absolute;
  bottom: 0;
  right: 0;
  left: 0;
  margin: auto;
  max-width: 1200px;
  padding-block-end: 24px;
`;

const CookieConsentsBannerRoot = styled.div`
  margin-block-start: auto;
  ${elevationCss(16)}
  border-radius: 8px;
  min-height: 118px;
  padding: 32px;
  display: flex;
  align-items: center;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    display: block;
    padding: 16px;
    padding-block-end: calc(16px + env(safe-area-inset-bottom));
  }
`;

const Description = styled.p`
  ${typographyCss.Body3}
  margin: 0;
  margin-inline-end: 32px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    margin-inline-end: 0;
    margin-block-end: 16px;
  }
`;

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;

  button + button {
    margin-inline-start: 16px;
  }

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    button {
      flex: 1;
    }
  }
`;

const CookieConsentsModal = ({
  className,
  onAcceptDefaultConsentsClick,
  onManageConsentsDialogClick,
  privacyPolicyUrl,
}: CookieConsentsBannerProps) => {
  return (
    <CookieConsentsBannerRoot data-testid="consent-banner-root" className={className}>
      <Description>
        <FormattedMessage
          id="gdpr-consents.banner.text"
          values={{
            a: chunks =>
              privacyPolicyUrl ? (
                <TextButtonLink href={privacyPolicyUrl} target="_blank" size="inherit">
                  {chunks}
                </TextButtonLink>
              ) : null,
          }}
        />
      </Description>

      <ButtonsContainer>
        <Button
          variant="lightBlue"
          size="small"
          onClick={onManageConsentsDialogClick}
          data-testid="consent-banner-manage-button"
        >
          <FormattedMessage id="gdpr-consents.banner.manage-cookies-button" />
        </Button>

        <Button
          variant="blue"
          size="small"
          onClick={onAcceptDefaultConsentsClick}
          data-testid="consent-banner-accept-button"
        >
          <FormattedMessage id="gdpr-consents.banner.accept-button" />
        </Button>
      </ButtonsContainer>
    </CookieConsentsBannerRoot>
  );
};

export const CookieConsentsBanner = ({
  className,
  show,
  onAcceptDefaultConsentsClick,
  onManageConsentsClick,
  privacyPolicyUrl,
}: CookieConsentsModalProps) => {
  return (
    <>
      {show && (
        <Root className={className}>
          <BannerContainer aria-live="assertive">
            <CookieConsentsModal
              privacyPolicyUrl={privacyPolicyUrl}
              onAcceptDefaultConsentsClick={onAcceptDefaultConsentsClick}
              onManageConsentsDialogClick={onManageConsentsClick}
            />
          </BannerContainer>
        </Root>
      )}
    </>
  );
};
